import React, { useState } from 'react';
import { Menu } from '@headlessui/react';
import cx from 'classnames';

import { IconCaret } from '..';
import { DropdownOption } from '.';
import { getBorderClasses, getIconFillColor } from './helpers';
import {
    borderShim,
    defaultBorderConfig,
    defaultDisabled,
    defaultSize,
    defaultHeights,
    defaultWidths,
    descriptionStyles,
    headerLeftPadding,
    iconWidths,
} from './constants';

import './dropdown.css';

/**
 * A dropdown menu which allows for one option to be selected at any time, like a <select> element.
 * Accepts a `size` prop which pre-configures styling based on a preset. If a `defaultIndex` is
 * provided, the option at that index will initially be selected; otherwise, the first option will
 * initially be selected. Border styling is set by the `borderConfig` prop, which can either be a
 * specific style (see JSDoc typedef for DropdownBorderConfig) or left undefined to set the border
 * styling based on the `size` prop.
 * @param {DropdownProps<V>} props
 */
export function Dropdown<V>(props: DropdownProps<V>): JSX.Element {
    const {
        borderConfig = defaultBorderConfig,
        boxShadowWhenClosed = false,
        boxShadowWhenOpen = true,
        dataCy,
        defaultIndex,
        description,
        disabled = defaultDisabled,
        externalState,
        expansionDirection = 'down',
        handleChange,
        options,
        size = defaultSize,
    } = props;
    const descriptionStyle = descriptionStyles[size];
    const height = props.height ?? defaultHeights[size];
    const width = props.width ?? defaultWidths[size];

    const internalState = useState(defaultIndex);
    const selectedIndex = externalState
        ? externalState.selectedIndex
        : internalState[0];
    const setSelectedIndex = externalState
        ? externalState.setSelectedIndex
        : internalState[1];

    const selectedOption = options.find(
        (option: DropdownOption<unknown>, index: number): boolean =>
            selectedIndex === index,
    );
    const borderClasses = getBorderClasses({
        borderConfig,
        size,
    });
    const iconFillColor = getIconFillColor({
        disabled,
        size,
    });
    const headerLeftPaddingClass = headerLeftPadding[size];
    const iconWidth = iconWidths[size];

    return (
        <div
            className={cx(
                'dropdown-v2 z-10 relative inline-block text-left',
                size,
            )}
            data-cy={dataCy}
            style={{ height, width }}
        >
            <Menu>
                {({ open }): JSX.Element => (
                    <>
                        <Menu.Button
                            disabled={disabled}
                            className={cx(
                                'dropdown-v2-header w-full h-full flex items-center justify-between respond-to-mouse',
                                {
                                    'shadow-lg': !open && boxShadowWhenClosed,
                                },
                                borderClasses,
                                headerLeftPaddingClass,
                            )}
                        >
                            {description && (
                                <div
                                    className={`${descriptionStyle} mr-4 flex-none`}
                                >
                                    {description}
                                </div>
                            )}
                            <div
                                className={cx(
                                    'truncate flex-grow text-left font-semibold',
                                    {
                                        'text-tertiary-400': disabled,
                                    },
                                )}
                            >
                                {selectedOption?.label}
                            </div>
                            <IconCaret
                                width={iconWidth}
                                fillColor={iconFillColor}
                                rotation={
                                    expansionDirection === 'down' ? 0 : 180
                                }
                            />
                        </Menu.Button>

                        <Menu.Items
                            className={cx(
                                'dropdown-v2-menu w-full absolute left-0 outline-none',
                                borderClasses,
                                size,
                                {
                                    'shadow-lg': open && boxShadowWhenOpen,
                                },
                            )}
                            style={{
                                top:
                                    expansionDirection === 'up'
                                        ? -(height * options.length) -
                                          borderShim
                                        : undefined,
                            }}
                        >
                            {options.map(
                                (
                                    option: DropdownOption<V>,
                                    index: number,
                                ): JSX.Element => (
                                    <DropdownOption
                                        key={`option.${option.label}`}
                                        handleChange={(): void => {
                                            setSelectedIndex(index);
                                            handleChange(option.value);
                                        }}
                                        height={height}
                                        disabled={disabled}
                                        option={option}
                                        size={size}
                                    />
                                ),
                            )}
                        </Menu.Items>
                    </>
                )}
            </Menu>
        </div>
    );
}
